import React from 'react';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CalendarDays,
} from 'lucide-react';
import { CATEGORIES } from './data';
import { DataHint } from './data-hint';
import {
  DEMO_DAY,
  DemoState,
  Trip,
  businessDay,
  duration,
  metrics,
  money,
  shift,
  sum,
  hasTripData,
  operationalGroups,
} from './model';

export type Drill = { title: string; trips: Trip[]; showRemaining?: boolean };
function Chart({
  items,
  onSelect,
  currency = false,
}: {
  items: {
    label: string;
    value: number;
    trips: Trip[];
    tone?: string;
    suffix?: string;
  }[];
  onSelect: (value: Drill) => void;
  currency?: boolean;
}) {
  const maximum = Math.max(1, ...items.map((item) => item.value));
  return (
    <div className="stay-chart">
      {items.map((item) => (
        <DataHint
          key={item.label}
          title={item.label}
          description={`${currency ? money(item.value) + ' em estadias apuradas' : Number(item.value.toFixed(1)).toLocaleString('pt-BR') + (item.suffix || ' viagens')} · ${item.trips.length} viagens compõem este dado. O comprimento da barra compara este valor ao maior valor do mesmo gráfico. Clique para conferir as viagens e os totais.`}
        >
          <button
            className="stay-chart-item"
            onClick={() => onSelect({ title: item.label, trips: item.trips })}
            aria-label={`Detalhar ${item.label}`}
          >
            <span>{item.label}</span>
            <div className="stay-chart-track">
              <i
                className={item.tone || 'green'}
                style={{ width: `${(item.value / maximum) * 100}%` }}
              />
            </div>
            <b>
              {currency
                ? money(item.value)
                : Number(item.value.toFixed(1)).toLocaleString('pt-BR')}
              {item.suffix || ''}
            </b>
          </button>
        </DataHint>
      ))}
    </div>
  );
}
export function MetricCard({
  title,
  value,
  detail,
  tone = '',
  onClick,
  icon,
  attention = false,
}: {
  title: string;
  value: React.ReactNode;
  detail: string;
  tone?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  attention?: boolean;
}) {
  return (
    <DataHint
      title={`${title} · ${value}`}
      description={`${detail}.${onClick ? ' Clique para abrir as viagens que compõem este indicador.' : ''}`}
    >
      <button
        className={`stay-stat ${tone}${attention ? ' stay-stat-attention' : ''}`}
        onClick={onClick}
        aria-disabled={!onClick}
      >
        <span>
          <span className="stay-stat-title">
            {icon}
            {title}
          </span>
          {onClick && <ArrowRight size={15} />}
        </span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </button>
    </DataHint>
  );
}

export function OperationalSummary({
  trips,
  onDrill,
}: {
  trips: Trip[];
  onDrill: (drill: Drill) => void;
}) {
  const groups = operationalGroups(trips);
  return (
    <section aria-label="Resumo da operação">
      <div className="stay-stat-grid">
        <MetricCard
          title="Em operação"
          value={groups.active.length}
          detail={`${groups.active.length} viagens em andamento na seleção`}
          onClick={() =>
            onDrill({ title: 'Viagens em operação', trips: groups.active })
          }
        />
        <MetricCard
          title="Dentro da franquia"
          value={groups.within.length}
          detail={`${groups.within.length} viagens · inclui ${groups.critical.length} no limite; não inclui vencidas`}
          tone="green"
          onClick={() =>
            onDrill({
              title: 'Viagens dentro da franquia',
              trips: groups.within,
            })
          }
        />
        <MetricCard
          title="Atenção: viagens próximas do vencimento"
          attention={groups.critical.length > 0}
          icon={
            <AlertTriangle
              size={20}
              aria-hidden="true"
              className="stay-attention-icon"
            />
          }
          value={groups.critical.length}
          detail={`${groups.critical.length} viagens ainda dentro da franquia, com até 2h restantes`}
          tone="amber"
          onClick={() =>
            onDrill({ title: 'Franquia no limite', trips: groups.critical })
          }
        />
        <MetricCard
          title="Franquia ultrapassada"
          value={groups.exceeded.length}
          detail={`${groups.exceeded.length} viagens vencidas · ${money(sum(groups.exceeded.map((trip) => metrics(trip).cents)))}`}
          tone="red"
          onClick={() =>
            onDrill({ title: 'Franquia ultrapassada', trips: groups.exceeded })
          }
        />
      </div>
    </section>
  );
}

export default function Indicators({
  state,
  rows,
  dimensionRows,
  onDrill,
  referenceDate = DEMO_DAY,
}: {
  state: DemoState;
  rows: Trip[];
  dimensionRows: Trip[];
  onDrill: (drill: Drill) => void;
  referenceDate?: string;
}) {
  const year = Number(referenceDate.slice(0, 4));
  const referenceMonth = Number(referenceDate.slice(5, 7));
  const currentMonthLabel = new Date(
    year,
    referenceMonth - 1,
    1
  ).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  const recentMonths = Array.from({ length: 6 }, (_, index) => {
    const month = new Date(year, referenceMonth - 6 + index, 1);
    return { month: month.getMonth() + 1, year: month.getFullYear() };
  });
  const charged = rows.filter((trip) => metrics(trip).cents > 0);
  const over = rows.filter((trip) => metrics(trip).total > 600);
  const invalidOver = over.filter((trip) => !metrics(trip).eligible);
  const total = sum(charged.map((trip) => metrics(trip).cents));
  const approvedIds = state.invoices
    .filter((invoice) => invoice.status === 'approved')
    .flatMap((invoice) => invoice.tripIds);
  const approved = charged.filter((trip) => approvedIds.includes(trip.id));
  const approvedTotal = sum(approved.map((trip) => metrics(trip).cents));
  const current = dimensionRows.filter(
    (trip) =>
      businessDay(trip.events.scheduled).startsWith(
        referenceDate.slice(0, 7)
      ) && metrics(trip).cents > 0
  );
  const monthLabel = (month: number) =>
    new Date(year, month - 1, 1)
      .toLocaleDateString('pt-BR', { month: 'short' })
      .replace('.', '');
  const monthly = (month: number, monthYear = year) =>
    dimensionRows.filter((trip) =>
      businessDay(trip.events.scheduled).startsWith(
        `${monthYear}-${String(month).padStart(2, '0')}`
      )
    );
  const bands = [
    { label: 'Até 3h', min: 0, max: 180 },
    { label: '> 3h até 4h', min: 180, max: 240 },
    { label: '> 4h até 5h', min: 240, max: 300 },
    { label: '> 5h até 6h', min: 300, max: 360 },
    { label: '> 6h até 7h', min: 360, max: 420 },
    { label: '> 7h até 8h', min: 420, max: 480 },
    { label: '> 8h até 10h', min: 480, max: 600 },
    { label: 'Acima de 10h', min: 600, max: Infinity },
  ];
  return (
    <div className="stay-analytics">
      <div className="stay-section-label">
        <BarChart3 size={17} />
        <div>
          <h2>Gestão à vista</h2>
          <p>
            Indicadores do período filtrado. Clique nos gráficos para conferir
            as viagens que compõem cada total.
          </p>
        </div>
      </div>
      <OperationalSummary trips={rows} onDrill={onDrill} />
      <div className="stay-stat-grid">
        <MetricCard
          title="Estadia no mês atual"
          value={money(sum(current.map((trip) => metrics(trip).cents)))}
          detail={`${currentMonthLabel} · filtros de unidade, categoria e transportadora`}
          onClick={() =>
            onDrill({ title: `Estadia · ${currentMonthLabel}`, trips: current })
          }
        />
        <MetricCard
          title="Sem aprovação"
          value={money(total - approvedTotal)}
          detail="Inclui apuração, validação e faturas pendentes ou reprovadas"
          tone="amber"
          onClick={() =>
            onDrill({
              title: 'Estadias sem aprovação',
              trips: charged.filter((trip) => !approvedIds.includes(trip.id)),
            })
          }
        />
        <MetricCard
          title="Aprovadas"
          value={money(approvedTotal)}
          detail="Valor com aprovação Corteva no período"
          tone="green"
          onClick={() =>
            onDrill({ title: 'Estadias aprovadas', trips: approved })
          }
        />
        <MetricCard
          title="Total do período"
          value={money(total)}
          detail={`${charged.length} viagens com valor de estadia apurado`}
          onClick={() =>
            onDrill({ title: 'Todas as estadias do período', trips: charged })
          }
        />
      </div>
      <div className="stay-analytics-grid">
        <section className="stay-card">
          <div className="stay-card-heading">
            <h3>Veículos acima de 10 horas</h3>
          </div>
          <p>
            Percentual por viagem · universo: {over.length} viagens acima de 10h
          </p>
          <div className="stay-comparison">
            <button
              onClick={() =>
                onDrill({ title: 'Acima de 10h · com estadia', trips: charged })
              }
            >
              <strong>
                {over.length
                  ? Math.round((charged.length / over.length) * 100)
                  : 0}
                %
              </strong>
              <span>Com estadia</span>
              <small>{charged.length} viagens</small>
            </button>
            <button
              onClick={() =>
                onDrill({
                  title: 'Acima de 10h · sem direito',
                  trips: invalidOver,
                })
              }
            >
              <strong>
                {over.length
                  ? Math.round((invalidOver.length / over.length) * 100)
                  : 0}
                %
              </strong>
              <span>Sem direito à estadia</span>
              <small>{invalidOver.length} viagens · chegada atrasada</small>
            </button>
          </div>
          <Chart
            items={bands.map((band) => {
              const trips = rows.filter(
                (trip) =>
                  (band.min === 0
                    ? metrics(trip).total >= 0
                    : metrics(trip).total > band.min) &&
                  metrics(trip).total <= band.max
              );
              return {
                label: band.label,
                value: trips.length,
                trips,
                tone: band.max === Infinity ? 'red' : 'green',
              };
            })}
            onSelect={onDrill}
          />
        </section>
        <section className="stay-card">
          <div className="stay-card-heading">
            <h3>Estadia mensal</h3>
          </div>
          <p>
            Últimos 6 meses até {currentMonthLabel} · permanência &gt; 10h, com
            e sem direito. Os filtros de período não limitam este histórico.
          </p>
          <div className="stay-columns">
            {recentMonths.map((period) => {
              const month = period.month;
              const trips = monthly(month, period.year).filter(
                (trip) => metrics(trip).total > 600
              );
              const maximum = Math.max(
                1,
                ...recentMonths.map(
                  (value) =>
                    monthly(value.month, value.year).filter(
                      (trip) => metrics(trip).total > 600
                    ).length
                )
              );
              return (
                <DataHint
                  key={month}
                  title={`Permanência acima de 10h · ${monthLabel(month)}/${period.year}`}
                  description={`${trips.length} viagens permaneceram mais de 10 horas neste mês, incluindo viagens com estadia e sem direito à cobrança. Clique para consultar placas, datas e tempos.`}
                >
                  <button
                    onClick={() =>
                      onDrill({
                        title: `Permanência > 10h · ${monthLabel(month)}/${period.year}`,
                        trips,
                      })
                    }
                  >
                    <b>{trips.length}</b>
                    <div>
                      <i
                        style={{ height: `${(trips.length / maximum) * 100}%` }}
                      />
                    </div>
                    <span>{monthLabel(month)}</span>
                  </button>
                </DataHint>
              );
            })}
          </div>
          <div className="stay-callout">
            <CalendarDays size={17} /> Clique em um mês para abrir o
            detalhamento de veículos, datas e permanência.
          </div>
        </section>
        <section className="stay-card">
          <div className="stay-card-heading">
            <h3>Performance por transportadora</h3>
          </div>
          <p>Quantidade de viagens com e sem incidência de estadia</p>
          <div className="stay-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Transportadora</th>
                  <th>Com estadia</th>
                  <th>Sem estadia</th>
                  <th>Horas excedentes</th>
                </tr>
              </thead>
              <tbody>
                {Array.from(
                  new Set(dimensionRows.map((trip) => trip.carrier))
                ).map((carrier) => {
                  const trips = rows.filter((trip) => trip.carrier === carrier);
                  return (
                    <tr key={carrier}>
                      <td>
                        <button
                          className="stay-link"
                          onClick={() => onDrill({ title: carrier, trips })}
                        >
                          {carrier}
                        </button>
                      </td>
                      <td>
                        {trips.filter((trip) => metrics(trip).cents > 0).length}
                      </td>
                      <td>
                        {
                          trips.filter((trip) => metrics(trip).cents === 0)
                            .length
                        }
                      </td>
                      <td>
                        {duration(
                          sum(trips.map((trip) => metrics(trip).excess))
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
        {dimensionRows.some(
          (trip) =>
            hasTripData(trip, 'Tonelagem') &&
            hasTripData(trip, 'Categoria de produto')
        ) && (
          <section className="stay-card">
            <div className="stay-card-heading">
              <h3>Categoria de produto & peso</h3>
            </div>
            <p>
              Somatória do peso das viagens com estadia · período selecionado
            </p>
            <Chart
              items={CATEGORIES.map((category) => {
                const trips = charged.filter(
                  (trip) => trip.category === category
                );
                return {
                  label: category,
                  value: sum(trips.map((trip) => trip.tons)),
                  trips,
                  suffix: ' t',
                };
              })}
              onSelect={onDrill}
            />
            <div className="stay-category-totals">
              {CATEGORIES.map((category) => (
                <span key={category}>
                  {category}
                  <b>
                    {
                      charged.filter((trip) => trip.category === category)
                        .length
                    }{' '}
                    estadias
                  </b>
                </span>
              ))}
            </div>
          </section>
        )}
        <section className="stay-card stay-wide">
          <div className="stay-card-heading">
            <h3>Valor da estadia · comparativo anual</h3>
          </div>
          <p>
            Janeiro a dezembro / {year} · valores apurados, com filtros de
            unidade, categoria e transportadora. Meses futuros sem dados.
          </p>
          <div className="stay-columns annual">
            {Array.from({ length: 12 }, (_, index) => index + 1).map(
              (month) => {
                const trips = monthly(month).filter(
                  (trip) => metrics(trip).cents > 0
                );
                const amount = sum(trips.map((trip) => metrics(trip).cents));
                const maximum = Math.max(
                  1,
                  ...Array.from({ length: 12 }, (_, index) =>
                    sum(monthly(index + 1).map((trip) => metrics(trip).cents))
                  )
                );
                return (
                  <DataHint
                    key={month}
                    title={`Estadia de ${monthLabel(month)}/${year} · ${money(amount)}`}
                    description={`${trips.length} viagens com valor de estadia apurado neste mês. Total proporcional aos minutos excedentes à franquia, à tarifa de R$ 46,62 por hora. Clique para detalhar as viagens.`}
                  >
                    <button
                      onClick={() =>
                        onDrill({
                          title: `Valor de estadia · ${monthLabel(month)}/${year}`,
                          trips,
                        })
                      }
                    >
                      <b>{money(amount)}</b>
                      <div>
                        <i style={{ height: `${(amount / maximum) * 100}%` }} />
                      </div>
                      <span>{monthLabel(month)}</span>
                    </button>
                  </DataHint>
                );
              }
            )}
          </div>
        </section>
        {dimensionRows.some((trip) => hasTripData(trip, 'Tipo de local')) && (
          <section className="stay-card">
            <div className="stay-card-heading">
              <h3>Fábrica & fazenda</h3>
            </div>
            <p>
              Classificação pela identificação fiscal da NF · CNPJ e logradouro
            </p>
            {(['Fábrica', 'Fazenda', 'Não informado'] as const).map((site) => {
              const trips = charged.filter((trip) => trip.site === site);
              const amount = sum(trips.map((trip) => metrics(trip).cents));
              return (
                <button
                  className="stay-site"
                  key={site}
                  onClick={() =>
                    onDrill({ title: `Estadias · ${site}`, trips })
                  }
                >
                  <span>
                    {site}
                    <small>{trips.length} viagens</small>
                  </span>
                  <strong>{money(amount)}</strong>
                </button>
              );
            })}
            <div className="stay-equation">
              Fábrica + Fazenda + Não informado = {money(total)}
              <br />
              Sem aprovação + Aprovadas = {money(total)}
            </div>
          </section>
        )}
        <section className="stay-card">
          <div className="stay-card-heading">
            <h3>Estadia por turno</h3>
          </div>
          <p>Turno definido pelo horário do agendamento, em Brasília</p>
          <Chart
            currency
            items={['Manhã', 'Tarde', 'Noite', 'Madrugada'].map((label) => {
              const trips = charged.filter((trip) => shift(trip) === label);
              return {
                label,
                value: sum(trips.map((trip) => metrics(trip).cents)),
                trips,
              };
            })}
            onSelect={onDrill}
          />
          <div className="stay-turns">
            Manhã 06:00–12:59 · Tarde 13:00–17:59
            <br />
            Noite 18:00–23:59 · Madrugada 00:00–05:59
          </div>
        </section>
      </div>
    </div>
  );
}
